import { useRef, useState } from 'react';
import {
	Box,
	Button,
	Container,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
	CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import { sendPublicSample } from '../../Shared/publicApi';

const SampleDemo = () => {
	const honeypotRef = useRef(null);
	const { enqueueSnackbar } = useSnackbar();
	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [sampleType, setSampleType] = useState('bid');
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async (event) => {
		event.preventDefault();
		const trimmedEmail = email.trim();
		if (!trimmedEmail) {
			enqueueSnackbar('Enter your email address.', { variant: 'warning' });
			return;
		}

		setSubmitting(true);
		try {
			const response = await sendPublicSample({
				email: trimmedEmail,
				name: name.trim(),
				sampleType,
				website: honeypotRef.current?.value ?? '',
			});
			enqueueSnackbar(
				response?.detail ||
					'If that email is valid, you should receive a sample shortly.',
				{ variant: 'success' },
			);
			setEmail('');
			setName('');
		} catch (error) {
			const message =
				error?.response?.data?.email?.[0] ||
				error?.response?.data?.detail ||
				error?.message ||
				'Something went wrong. Please try again.';
			enqueueSnackbar(message, { variant: 'error' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Box
			id="sample-demo"
			component="section"
			sx={{
				py: { xs: 7, md: 10 },
				bgcolor: 'background.paper',
				borderTop: '1px solid',
				borderBottom: '1px solid',
				borderColor: 'divider',
				scrollMarginTop: { xs: 72, md: 80 },
			}}
		>
			<Container maxWidth="md">
				<Box sx={{ textAlign: 'center', mb: 4 }}>
					<Typography
						variant="overline"
						color="warning.main"
						sx={{ fontWeight: 700, letterSpacing: '0.14em' }}
					>
						See it yourself
					</Typography>
					<Typography
						variant="h4"
						component="h2"
						sx={{ mt: 1, fontWeight: 700 }}
					>
						Get a real sample in your inbox
					</Typography>
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{ mt: 2, maxWidth: 560, mx: 'auto', lineHeight: 1.8 }}
					>
						We&apos;ll send a branded proposal from Ridgeline Trade Co. — a
						sample subcontractor — so you can see the one-click Accept /
						Decline your customers get. Unanswered bids get the same treatment
						as a reminder, automatically.
					</Typography>
				</Box>

				<Paper
					elevation={0}
					component="form"
					onSubmit={handleSubmit}
					sx={{
						p: { xs: 3, md: 4 },
						borderRadius: 4,
						border: '1px solid',
						borderColor: 'divider',
						position: 'relative',
					}}
				>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								label="Your email"
								type="email"
								value={email}
								onChange={(event) => setEmail(event.target.value)}
								fullWidth
								required
								autoComplete="email"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								label="Your name (optional)"
								value={name}
								onChange={(event) => setName(event.target.value)}
								fullWidth
								autoComplete="name"
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
								<Button
									type="button"
									variant={sampleType === 'bid' ? 'contained' : 'outlined'}
									onClick={() => setSampleType('bid')}
									fullWidth
								>
									Sample proposal
								</Button>
								<Button
									type="button"
									variant={sampleType === 'invoice' ? 'contained' : 'outlined'}
									onClick={() => setSampleType('invoice')}
									fullWidth
								>
									Sample invoice
								</Button>
							</Stack>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Button
								type="submit"
								variant="contained"
								color="warning"
								size="large"
								fullWidth
								disabled={submitting}
								startIcon={
									submitting ? (
										<CircularProgress size={18} color="inherit" />
									) : (
										<SendIcon />
									)
								}
							>
								{submitting ? 'Sending...' : 'Send me the sample'}
							</Button>
						</Grid>
					</Grid>
					{/* Honeypot — hidden from users, bots may fill it */}
					<Box
						component="input"
						ref={honeypotRef}
						name="website"
						tabIndex={-1}
						autoComplete="off"
						aria-hidden="true"
						sx={{
							position: 'absolute',
							left: -9999,
							opacity: 0,
							height: 0,
							width: 0,
						}}
					/>
				</Paper>
			</Container>
		</Box>
	);
};

export default SampleDemo;
